// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  engineApiV2,
  type EngineTagTypes,
} from '@shared/data-access/api-base/citec-engine-api';
import type { FetchQueryFn } from '@shared/data-access/queries';
import type * as EngineTypes from '@shared/data-access/types/engine-types';
import type { SharedTagTypes } from '@shared/data-access/utils';

type EndpointBuilders = EndpointBuilder<
  FetchQueryFn,
  EngineTagTypes | SharedTagTypes,
  'engineApiV2'
>;

/**
 * Raw Engine API – Tags, Tag Assignments, Illiquid Assets
 *
 * Direct access to the engine tags, tag-assignments, and illiquid-assets REST
 * endpoints without data transformations, business logic, or multiple request
 * orchestration. These raw endpoints serve as the foundation for processed
 * endpoints (e.g. tags-api) that add cache tags, invalidation, and
 * queryFn/onQueryStarted.
 *
 * @remarks
 * **Key Characteristics:**
 *
 * - All endpoints use simple `query` functions (no `queryFn` or complex logic)
 * - Direct API calls with no data transformations
 * - Not imported or used elsewhere; only endpoints with queryFn/onQueryStarted (e.g.
 *   illiquids-api) invoke this layer via `rawApi.endpoints.xxx.initiate()`
 * - Endpoints are grouped in `getTagsEndpoints(build)`,
 *   `getIlliquidAssetsEndpoints(build)`, and composed in `endpoints: build => ({
 *   ...getTagsEndpoints(build), ...getIlliquidAssetsEndpoints(build) })`.
 *
 * **Usage Pattern:** Processed API files (tags-api) use these raw endpoints
 * internally in queryFn and add cache tag management (providesTags,
 * invalidatesTags). Same pattern as brokerage raw-api and advisory-api.v2.
 * @example
 *
 * ```tsx
 * // In tags-api (processed layer)
 * getTags: build.query({
 *   queryFn: async (params, api) => {
 *     const result = await api.dispatch(
 *       rawApi.endpoints.getTagsRaw.initiate(params),
 *     );
 *     if (result.error) return { error: result.error };
 *     return { data: result.data };
 *   },
 *   providesTags: result => [...],
 * }),
 * ```
 */
const getTagsEndpoints = (build: EndpointBuilders) => ({
  /**
   * List tags.
   *
   * List of user's tags by owner type. Query: owner_type (required).
   *
   * **Raw endpoint:** `GET /tags/`
   *
   * @param params - Query `owner_type`. See {@link EngineTypes.ListTagsParams}.
   * @returns {@link EngineTypes.ListTags}
   */
  getTagsRaw: build.query<EngineTypes.ListTags, EngineTypes.ListTagsParams>({
    query: params => ({
      url: '/tags/',
      method: 'GET',
      params,
    }),
  }),

  /**
   * Create a tag.
   *
   * Add a new tag. Body: owner_type, name.
   *
   * **Raw endpoint:** `POST /tags/`
   *
   * @param body - Request body. See {@link EngineTypes.CreateTag}.
   * @returns The created {@link EngineTypes.Tag}.
   */
  createTagRaw: build.mutation<EngineTypes.Tag, EngineTypes.CreateTag>({
    query: body => ({
      url: '/tags/',
      method: 'POST',
      body,
    }),
  }),

  /**
   * Delete a tag by id.
   *
   * Delete a tag owned by the user. Path: tag_id. Query: owner_type (required),
   * force (optional).
   *
   * **Raw endpoint:** `DELETE /tags/{tag_id}/`
   *
   * @param arg - Path `tag_id` and query `owner_type`, `force`. See
   *   {@link EngineTypes.DeleteTagParams}.
   */
  deleteTagRaw: build.mutation<void, EngineTypes.DeleteTagParams>({
    query: ({ tag_id, owner_type, force }) => ({
      url: `/tags/${tag_id}/`,
      method: 'DELETE',
      params: { owner_type, force },
    }),
  }),

  /**
   * Sync tags for an object.
   *
   * Sets the full list of tag IDs for an object. Body: owner_type, object_type,
   * object_id, tag_ids. Response: single ListTagAssignments (object_type,
   * object_id, tags).
   *
   * **Raw endpoint:** `PUT /tag-assignments/sync/`
   *
   * @param body - Request body. See {@link EngineTypes.SyncTagAssignments}.
   * @returns {@link EngineTypes.ListTagAssignments}
   */
  syncTagsRaw: build.mutation<
    EngineTypes.ListTagAssignments,
    EngineTypes.SyncTagAssignments
  >({
    query: body => ({
      url: '/tag-assignments/sync/',
      method: 'PUT',
      body,
    }),
  }),

  /**
   * Search tag assignments.
   *
   * Search tags related to a specific object. POST /tag-assignments/ with body
   * owner_type, optional object_type/object_id. Returns ListSearchTags (data,
   * count).
   *
   * **Raw endpoint:** `POST /tag-assignments/`
   *
   * @param body - Request body. See {@link EngineTypes.SearchTagAssignments}.
   * @returns {@link EngineTypes.ListSearchTags}
   */
  searchTagAssignmentsRaw: build.mutation<
    EngineTypes.ListSearchTags,
    EngineTypes.SearchTagAssignments
  >({
    query: body => ({
      url: '/tag-assignments/',
      method: 'POST',
      body,
    }),
  }),
});

const getIlliquidAssetsEndpoints = (build: EndpointBuilders) => ({
  /**
   * List illiquid assets.
   *
   * **Raw endpoint:** `GET /citec/illiquid-assets/`
   */
  listIlliquidAssetsRaw: build.query<
    EngineTypes.IlliquidAssetsListResponse,
    EngineTypes.ListIlliquidAssetsParams
  >({
    query: params => ({
      url: '/citec/illiquid-assets/',
      method: 'GET',
      params,
    }),
  }),

  /**
   * Create an illiquid asset.
   *
   * **Raw endpoint:** `POST /citec/illiquid-assets/`
   */
  createIlliquidAssetRaw: build.mutation<
    EngineTypes.IlliquidAssetResponse,
    {
      body: EngineTypes.CreateIlliquidAssetRequestBody;
    } & EngineTypes.ListIlliquidAssetsParams
  >({
    query: ({ body, client_id }) => ({
      url: '/citec/illiquid-assets/',
      method: 'POST',
      body,
      params: { client_id },
    }),
  }),

  /**
   * Delete an illiquid asset.
   *
   * **Raw endpoint:** `DELETE /citec/illiquid-assets/{asset_id}/`
   */
  deleteIlliquidAssetRaw: build.mutation<
    void,
    EngineTypes.DeleteIlliquidAssetParams
  >({
    query: ({ asset_id, client_id }) => ({
      url: `/citec/illiquid-assets/${asset_id}/`,
      method: 'DELETE',
      params: { client_id },
    }),
  }),

  /**
   * Update an illiquid asset.
   *
   * **Raw endpoint:** `PATCH /citec/illiquid-assets/{asset_id}/`
   */
  updateIlliquidAssetRaw: build.mutation<
    EngineTypes.IlliquidAssetResponse,
    EngineTypes.UpdateIlliquidAssetParams
  >({
    query: ({ asset_id, body, client_id }) => ({
      url: `/citec/illiquid-assets/${asset_id}/`,
      method: 'PATCH',
      body,
      params: { client_id },
    }),
  }),

  /**
   * Create an illiquid asset event.
   *
   * **Raw endpoint:** `POST /citec/illiquid-assets/{asset_id}/events/`
   */
  createIlliquidAssetEventRaw: build.mutation<
    EngineTypes.IlliquidAssetEventResponse,
    EngineTypes.CreateIlliquidAssetEventParams
  >({
    query: ({ asset_id, body, client_id }) => ({
      url: `/citec/illiquid-assets/${asset_id}/events/`,
      method: 'POST',
      body,
      params: { client_id },
    }),
  }),

  /**
   * Delete an illiquid asset event.
   *
   * **Raw endpoint:** `DELETE /citec/illiquid-assets/{asset_id}/events/{event_id}/`
   */
  deleteIlliquidAssetEventRaw: build.mutation<
    void,
    EngineTypes.DeleteIlliquidAssetEventParams
  >({
    query: ({ asset_id, event_id, client_id }) => ({
      url: `/citec/illiquid-assets/${asset_id}/events/${event_id}/`,
      method: 'DELETE',
      params: { client_id },
    }),
  }),
});

const getDocumentsEndpoints = (build: EndpointBuilders) => ({
  /**
   * List documents.
   *
   * **Raw endpoint:** `GET /documents/`
   */
  listDocumentsRaw: build.query<
    EngineTypes.UserDocumentsListResponse,
    EngineTypes.ListDocumentsParams
  >({
    query: () => ({
      url: '/documents/',
      method: 'GET',
    }),
  }),

  /**
   * Fetch signed download URLs for a document (not cached — URLs expire).
   *
   * **Raw endpoint:** `GET /documents/{document_id}/`
   */
  fetchDocumentDetailRaw: build.mutation<
    EngineTypes.UserDocumentDetailResponse,
    EngineTypes.GetDocumentParams
  >({
    query: ({ document_id }) => ({
      url: `/documents/${document_id}/`,
      method: 'GET',
    }),
  }),

  /**
   * Create document (multipart).
   *
   * **Raw endpoint:** `POST /documents/`
   */
  createDocumentRaw: build.mutation<
    EngineTypes.UserDocumentResponse,
    EngineTypes.CreateDocumentMutationParams
  >({
    query: ({ body }) => {
      const formData = new FormData();

      formData.append('metadata', body.metadata);

      body.files.forEach(file => {
        formData.append('files', file);
      });

      return {
        url: '/documents/',
        method: 'POST',
        body: formData,
      };
    },
    extraOptions: {
      requestType: 'formData',
    },
  }),

  /**
   * Update document metadata and entity relations.
   *
   * **Raw endpoint:** `PUT /documents/{document_id}/`
   */
  updateDocumentRaw: build.mutation<
    EngineTypes.UserDocumentResponse,
    EngineTypes.UpdateDocumentMutationParams
  >({
    query: ({ document_id, body }) => ({
      url: `/documents/${document_id}/`,
      method: 'PUT',
      body,
    }),
  }),

  /**
   * Delete document.
   *
   * **Raw endpoint:** `DELETE /documents/{document_id}/`
   */
  deleteDocumentRaw: build.mutation<void, EngineTypes.DeleteDocumentParams>({
    query: ({ document_id, force }) => ({
      url: `/documents/${document_id}/`,
      method: 'DELETE',
      params: { force: force ?? undefined },
    }),
  }),
});

export const rawApi = engineApiV2.injectEndpoints({
  endpoints: build => ({
    ...getTagsEndpoints(build),
    ...getIlliquidAssetsEndpoints(build),
    ...getDocumentsEndpoints(build),
  }),
});
